import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTransfer } from '../contexts/TransferContext';

interface SessionManagerProps {
  children: React.ReactNode;
  onSessionExpired: () => void;
  idleTimeout?: number; // in minutes, default 15
}

// Session configuration
const SESSION_CONFIG = {
  IDLE_TIMEOUT_MINUTES: 15,
  WARNING_BEFORE_TIMEOUT_SECONDS: 60,
  TOKEN_CHECK_INTERVAL_MINUTES: 5,
};

export function SessionManager({
  children,
  onSessionExpired,
  idleTimeout = SESSION_CONFIG.IDLE_TIMEOUT_MINUTES,
}: SessionManagerProps) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(SESSION_CONFIG.WARNING_BEFORE_TIMEOUT_SECONDS);

  const { isTransferActive } = useTransfer();

  // Handle idle timeout
  const handleIdle = useCallback(() => {
    setShowWarning(false);
    onSessionExpired();
  }, [onSessionExpired]);

  // Handle warning before timeout — suppressed while a transfer is active
  const handleWarning = useCallback(() => {
    if (isTransferActive) return;
    setShowWarning(true);
    setCountdown(SESSION_CONFIG.WARNING_BEFORE_TIMEOUT_SECONDS);
  }, [isTransferActive]);

  const { resetTimer } = useIdleTimeout({
    timeout: idleTimeout * 60 * 1000, // Convert to milliseconds
    onIdle: handleIdle,
    onWarning: handleWarning,
    warningTime: SESSION_CONFIG.WARNING_BEFORE_TIMEOUT_SECONDS * 1000,
    isTransferActive,
  });

  // Hide the warning overlay immediately when a transfer starts
  useEffect(() => {
    if (isTransferActive && showWarning) {
      setShowWarning(false);
    }
  }, [isTransferActive, showWarning]);

  // Countdown timer for warning dialog
  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning]);

  // Periodic token validation
  useEffect(() => {
    const checkTokenValidity = async () => {
      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.accessToken) {
          onSessionExpired();
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        onSessionExpired();
      }
    };

    // Check token validity periodically
    const interval = setInterval(
      checkTokenValidity,
      SESSION_CONFIG.TOKEN_CHECK_INTERVAL_MINUTES * 60 * 1000
    );

    return () => clearInterval(interval);
  }, [onSessionExpired]);

  // Handle continue session
  const handleContinue = () => {
    setShowWarning(false);
    resetTimer();
  };

  // Handle logout
  const handleLogout = () => {
    setShowWarning(false);
    onSessionExpired();
  };

  return (
    <>
      {children}

      {/* Session Timeout Warning Modal */}
      {showWarning && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4"
            >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Session Timeout Warning
                </h3>
                <p className="text-sm text-gray-500">
                  Your session is about to expire
                </p>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Due to inactivity, you will be automatically logged out in{' '}
              <span className="font-bold text-red-600">{countdown}</span> seconds.
            </p>

            <p className="text-sm text-gray-500 mb-6">
              Click "Continue Session" to stay logged in, or "Logout" to end your
              session now.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleContinue}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Continue Session
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Logout
              </button>
            </div>
            </motion.div>
          </AnimatePresence>
        </div>
      , document.body)}
    </>
  );
}

export default SessionManager;
